/**
 * Tile layout arithmetic: grid units to pixels, pixels back to grid units, and
 * what happens when a dragged tile lands on top of another one.
 *
 * Pure and free of DOM so the awkward parts - the rounding, and the collision
 * cascade - are testable without a browser. `dashboard-grid.ts` owns the pointer
 * handling and calls in here for every decision.
 *
 * Positions are absolute rather than a CSS `grid-auto-flow`: uPlot is told an
 * explicit pixel size, so the layout has to be something this module can compute
 * exactly, not something the browser decides afterwards.
 */

import {
  GRID_COLS,
  GRID_GAP,
  ROW_HEIGHT,
  MIN_TILE_W,
  MIN_TILE_H,
  overlaps,
  type DashboardTile,
  type TilePos,
} from './dashboard-state.ts';

export interface PixelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Width of one column, given the host's inner width. */
export function columnWidth(hostWidth: number): number {
  return (hostWidth - GRID_GAP * (GRID_COLS - 1)) / GRID_COLS;
}

/** Where a tile sits, in px, for a host of the given width. */
export function tileRect(pos: TilePos, hostWidth: number): PixelRect {
  const col = columnWidth(hostWidth);
  return {
    left: pos.x * (col + GRID_GAP),
    top: pos.y * (ROW_HEIGHT + GRID_GAP),
    width: pos.w * col + (pos.w - 1) * GRID_GAP,
    height: pos.h * ROW_HEIGHT + (pos.h - 1) * GRID_GAP,
  };
}

/** Rows occupied by the layout, ending at the lowest tile. */
export function gridRows(tiles: DashboardTile[]): number {
  return tiles.reduce((m, t) => Math.max(m, t.pos.y + t.pos.h), 0);
}

export function gridHeightPx(tiles: DashboardTile[]): number {
  const rows = gridRows(tiles);
  return rows === 0 ? 0 : rows * ROW_HEIGHT + (rows - 1) * GRID_GAP;
}

/** Round a pixel delta to whole columns / rows. */
export function deltaToGrid(
  dxPx: number,
  dyPx: number,
  hostWidth: number,
): { dx: number; dy: number } {
  const col = columnWidth(hostWidth);
  return {
    dx: Math.round(dxPx / (col + GRID_GAP)),
    dy: Math.round(dyPx / (ROW_HEIGHT + GRID_GAP)),
  };
}

/** Move `pos` by whole grid steps, kept inside the grid and off negative rows. */
export function movedPos(pos: TilePos, dx: number, dy: number): TilePos {
  return {
    ...pos,
    x: Math.max(0, Math.min(GRID_COLS - pos.w, pos.x + dx)),
    y: Math.max(0, pos.y + dy),
  };
}

/** Resize `pos` by whole grid steps, respecting the minimums and the right edge. */
export function resizedPos(pos: TilePos, dw: number, dh: number, minH = MIN_TILE_H): TilePos {
  const w = Math.max(MIN_TILE_W, Math.min(GRID_COLS - pos.x, pos.w + dw));
  const h = Math.max(minH, pos.h + dh);
  return { ...pos, w, h };
}

/**
 * Make room for a moved tile: whatever it lands on falls straight down.
 *
 * Downward only, and never sideways: a tile that jumped columns to make room
 * would move somewhere the user was not looking, and the layout they let go of
 * would not be the one they got. Falling straight down is the one displacement
 * that stays predictable, and it is what Grafana does.
 *
 * Implemented as an ordered re-placement rather than a push cascade: tiles are
 * placed one at a time, each into the first free row at its own column, against
 * everything already placed - which cannot produce an overlap at all. A cascade
 * that pushed each collider down in turn looked simpler but was wrong: two tiles
 * displaced by the same anchor could be pushed onto *each other*.
 *
 * **The order is what makes a tile movable downward.** Placing the moved tile
 * first always keeps it where it was dropped and pushes the rest below it -
 * fine moving up, wrong moving down: the tile it passed would be pushed under
 * it, and `compactUp` would float the moved tile straight back to the top. So a
 * tile stays *ahead* of the moved one - placed first, keeping its place - when
 * the moved tile has not climbed past its middle, or has moved down past its
 * middle. Both directions swap at the same halfway point. `from` is where the
 * drag started; without it only the first rule applies.
 *
 * Returns a new array; the input is not mutated.
 */
export function resolveCollisions(
  tiles: DashboardTile[],
  movedId: string,
  from?: TilePos,
): Array<{ id: string; pos: TilePos }> {
  const moved = tiles.find((t) => t.id === movedId);
  const others = tiles
    .filter((t) => t.id !== movedId)
    .sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x);

  const movingDown = !!moved && !!from && moved.pos.y > from.y;
  const ahead = (t: DashboardTile): boolean => {
    if (!moved) return false;
    const middle = t.pos.y + t.pos.h / 2;
    if (middle < moved.pos.y) return true;
    return movingDown && t.pos.y >= from!.y && middle <= moved.pos.y + moved.pos.h;
  };

  const order: DashboardTile[] = [
    ...others.filter(ahead),
    ...(moved ? [moved] : []),
    ...others.filter((t) => !ahead(t)),
  ];

  const placed: TilePos[] = [];
  const out = new Map<string, TilePos>();
  for (const tile of order) {
    const pos = { ...tile.pos };
    while (placed.some((p) => overlaps(p, pos))) pos.y++;
    placed.push(pos);
    out.set(tile.id, pos);
  }

  return tiles.map((t) => ({ id: t.id, pos: out.get(t.id) ?? t.pos }));
}

/**
 * The whole layout after `movedId` has moved (or been resized) from `from`:
 * collisions resolved, then - when the dashboard floats its tiles - everything
 * pulled up into the space above it. What the drop placeholder shows, and what
 * a release commits.
 */
export function layoutAfterMove(
  tiles: DashboardTile[],
  movedId: string,
  from: TilePos,
  float: boolean,
): Array<{ id: string; pos: TilePos }> {
  const resolved = resolveCollisions(tiles, movedId, from);
  return float ? compactUp(resolved) : resolved;
}

/**
 * Pull every tile up into the space above it, keeping relative order.
 *
 * Run after a move or a delete so a layout does not accumulate holes. Tiles are
 * compacted in reading order - top to bottom, then left to right - so a tile
 * never overtakes one that was above it.
 */
export function compactUp(
  entries: Array<{ id: string; pos: TilePos }>,
): Array<{ id: string; pos: TilePos }> {
  const ordered = [...entries].sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x);
  const placed: TilePos[] = [];
  const out = new Map<string, TilePos>();

  for (const entry of ordered) {
    const pos = { ...entry.pos };
    while (pos.y > 0 && !placed.some((p) => overlaps(p, { ...pos, y: pos.y - 1 }))) {
      pos.y--;
    }
    placed.push(pos);
    out.set(entry.id, pos);
  }

  return entries.map((e) => ({ id: e.id, pos: out.get(e.id) ?? e.pos }));
}
